// CoursesContact.js - Online courses contact section
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates an online courses contact section with form and contact info
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The contact section elements
 */
export const createCoursesContactElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Class names
  const sectionClass = 'courses-contact-section';
  const containerClass = 'courses-contact-container';
  const headerClass = 'courses-contact-header';
  const titleClass = 'courses-contact-title';
  const subtitleClass = 'courses-contact-subtitle';
  const contentClass = 'courses-contact-content';
  const leftColumnClass = 'courses-contact-left';
  const rightColumnClass = 'courses-contact-right';
  
  // Contact info classes
  const infoContainerClass = 'courses-contact-info-container';
  const infoItemClass = 'courses-contact-info-item';
  const infoIconClass = 'courses-contact-info-icon';
  const infoContentClass = 'courses-contact-info-content';
  const infoTitleClass = 'courses-contact-info-title';
  const infoTextClass = 'courses-contact-info-text';
  
  // Form classes
  const formContainerClass = 'courses-contact-form-container';
  const formTitleClass = 'courses-contact-form-title';
  const formClass = 'courses-contact-form';
  const formRowClass = 'courses-contact-form-row';
  const formGroupClass = 'courses-contact-form-group';
  const formLabelClass = 'courses-contact-form-label';
  const formInputClass = 'courses-contact-form-input';
  const formTextareaClass = 'courses-contact-form-textarea';
  const formButtonClass = 'courses-contact-form-button';
  
  // Social classes
  const socialContainerClass = 'courses-contact-social-container';
  const socialTitleClass = 'courses-contact-social-title';
  const socialLinksClass = 'courses-contact-social-links';
  const socialLinkClass = 'courses-contact-social-link';

  // Desktop Styles
  const sectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '40px',
    paddingRight: '40px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 20% 30%, rgba(99, 102, 241, 0.08) 0%, transparent 60%),
      radial-gradient(circle at 80% 70%, rgba(139, 92, 246, 0.06) 0%, transparent 60%),
      linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)
    `,
    color: '#1e293b',
    overflow: 'hidden'
  };

  const containerStyles = {
    maxWidth: '1400px',
    marginLeft: 'auto',
    marginRight: 'auto',
    position: 'relative',
    zIndex: '2'
  };

  const headerStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const titleStyles = {
    fontSize: '56px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: '-0.02em',
    lineHeight: '1.1'
  };

  const subtitleStyles = {
    fontSize: '20px',
    color: '#64748b',
    lineHeight: '1.6',
    maxWidth: '600px',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontWeight: '400',
    fontFamily: '"Inter", sans-serif'
  };

  const contentStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'start'
  };

  const leftColumnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '50px'
  };

  const rightColumnStyles = {
    display: 'flex',
    flexDirection: 'column'
  };

  const infoContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  };

  const infoItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '20px',
    padding: '30px',
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s ease'
  };

  const infoIconStyles = {
    width: '50px',
    height: '50px',
    backgroundColor: '#6366f1',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: '#ffffff',
    flexShrink: '0'
  };

  const infoContentStyles = {
    flex: '1'
  };

  const infoTitleStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif'
  };

  const infoTextStyles = {
    fontSize: '16px',
    color: '#64748b',
    lineHeight: '1.5',
    fontFamily: '"Inter", sans-serif'
  };

  const formContainerStyles = {
    backgroundColor: '#ffffff',
    borderRadius: '20px',
    padding: '50px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
  };

  const formTitleStyles = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '40px',
    textAlign: 'center',
    fontFamily: '"Inter", sans-serif'
  };

  const formStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  };

  const formRowStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px'
  };

  const formGroupStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  };

  const formLabelStyles = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    fontFamily: '"Inter", sans-serif'
  };

  const formInputStyles = {
    padding: '16px 20px',
    backgroundColor: '#f9fafb',
    border: '1px solid #d1d5db',
    borderRadius: '12px',
    color: '#1e293b',
    fontSize: '16px',
    fontFamily: '"Inter", sans-serif',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const formTextareaStyles = {
    padding: '16px 20px',
    backgroundColor: '#f9fafb',
    border: '1px solid #d1d5db',
    borderRadius: '12px',
    color: '#1e293b',
    fontSize: '16px',
    fontFamily: '"Inter", sans-serif',
    minHeight: '120px',
    resize: 'vertical',
    transition: 'all 0.3s ease',
    outline: 'none'
  };

  const formButtonStyles = {
    padding: '18px 40px',
    backgroundColor: '#6366f1',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    alignSelf: 'flex-start'
  };

  const socialContainerStyles = {
    textAlign: 'center'
  };

  const socialTitleStyles = {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '30px',
    fontFamily: '"Inter", sans-serif'
  };

  const socialLinksStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px'
  };

  const socialLinkStyles = {
    width: '50px',
    height: '50px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6366f1',
    fontSize: '20px',
    transition: 'all 0.3s ease',
    border: '1px solid #e2e8f0',
    boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  };

  // Tablet Styles
  const tabletContentStyles = {
    gridTemplateColumns: '1fr',
    gap: '60px'
  };

  const tabletFormRowStyles = {
    gridTemplateColumns: '1fr'
  };

  const tabletTitleStyles = {
    fontSize: '48px'
  };

  // Mobile Styles
  const mobileSectionStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px'
  };

  const mobileTitleStyles = {
    fontSize: '36px'
  };

  const mobileFormContainerStyles = {
    padding: '30px 20px'
  };

  const mobileFormTitleStyles = {
    fontSize: '24px'
  };

  // Register styles
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(sectionClass, sectionStyles);
    newRegistry[DEVICES.DESKTOP].set(containerClass, containerStyles);
    newRegistry[DEVICES.DESKTOP].set(headerClass, headerStyles);
    newRegistry[DEVICES.DESKTOP].set(titleClass, titleStyles);
    newRegistry[DEVICES.DESKTOP].set(subtitleClass, subtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(contentClass, contentStyles);
    newRegistry[DEVICES.DESKTOP].set(leftColumnClass, leftColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(rightColumnClass, rightColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(infoContainerClass, infoContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(infoItemClass, infoItemStyles);
    newRegistry[DEVICES.DESKTOP].set(infoIconClass, infoIconStyles);
    newRegistry[DEVICES.DESKTOP].set(infoContentClass, infoContentStyles);
    newRegistry[DEVICES.DESKTOP].set(infoTitleClass, infoTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(infoTextClass, infoTextStyles);
    newRegistry[DEVICES.DESKTOP].set(formContainerClass, formContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(formTitleClass, formTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(formClass, formStyles);
    newRegistry[DEVICES.DESKTOP].set(formRowClass, formRowStyles);
    newRegistry[DEVICES.DESKTOP].set(formGroupClass, formGroupStyles);
    newRegistry[DEVICES.DESKTOP].set(formLabelClass, formLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(formInputClass, formInputStyles);
    newRegistry[DEVICES.DESKTOP].set(formTextareaClass, formTextareaStyles);
    newRegistry[DEVICES.DESKTOP].set(formButtonClass, formButtonStyles);
    newRegistry[DEVICES.DESKTOP].set(socialContainerClass, socialContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(socialTitleClass, socialTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(socialLinksClass, socialLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(socialLinkClass, socialLinkStyles);
    
    // Tablet styles
    newRegistry[DEVICES.TABLET].set(contentClass, tabletContentStyles);
    newRegistry[DEVICES.TABLET].set(formRowClass, tabletFormRowStyles);
    newRegistry[DEVICES.TABLET].set(titleClass, tabletTitleStyles);
    
    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(sectionClass, mobileSectionStyles);
    newRegistry[DEVICES.MOBILE].set(titleClass, mobileTitleStyles);
    newRegistry[DEVICES.MOBILE].set(formContainerClass, mobileFormContainerStyles);
    newRegistry[DEVICES.MOBILE].set(formTitleClass, mobileFormTitleStyles);
    

    return newRegistry;
  });

  // Add classes to global registry
  setGlobalClassNames(prev => new Set([
    ...prev,
    sectionClass, containerClass, headerClass, titleClass, subtitleClass,
    contentClass, leftColumnClass, rightColumnClass, infoContainerClass,
    infoItemClass, infoIconClass, infoContentClass, infoTitleClass, infoTextClass,
    formContainerClass, formTitleClass, formClass, formRowClass, formGroupClass,
    formLabelClass, formInputClass, formTextareaClass, formButtonClass,
    socialContainerClass, socialTitleClass, socialLinksClass, socialLinkClass
  ]));

  // Generate unique IDs
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  const contentId = sectionId + 5;
  const leftColumnId = sectionId + 6;
  const rightColumnId = sectionId + 7;
  
  // Contact info IDs
  const infoContainerId = sectionId + 8;
  const addressItemId = sectionId + 9;
  const addressIconId = sectionId + 10;
  const addressContentId = sectionId + 11;
  const addressTitleId = sectionId + 12;
  const addressTextId = sectionId + 13;
  
  const phoneItemId = sectionId + 14;
  const phoneIconId = sectionId + 15;
  const phoneContentId = sectionId + 16;
  const phoneTitleId = sectionId + 17;
  const phoneTextId = sectionId + 18;
  
  const emailItemId = sectionId + 19;
  const emailIconId = sectionId + 20;
  const emailContentId = sectionId + 21;
  const emailTitleId = sectionId + 22;
  const emailTextId = sectionId + 23;
  
  // Social IDs
  const socialContainerId = sectionId + 24;
  const socialTitleId = sectionId + 25;
  const socialLinksId = sectionId + 26;
  const linkedinLinkId = sectionId + 27;
  const twitterLinkId = sectionId + 28;
  const youtubeLinkId = sectionId + 29;
  
  // Form IDs
  const formContainerId = sectionId + 30;
  const formTitleId = sectionId + 31;
  const formId = sectionId + 32;
  
  const nameEmailRowId = sectionId + 33;
  const nameGroupId = sectionId + 34;
  const nameLabelId = sectionId + 35;
  const nameInputId = sectionId + 36;
  const emailGroupId = sectionId + 37;
  const emailLabelId = sectionId + 38;
  const emailInputId = sectionId + 39;
  
  const subjectGroupId = sectionId + 40;
  const subjectLabelId = sectionId + 41;
  const subjectInputId = sectionId + 42;
  
  const messageGroupId = sectionId + 43;
  const messageLabelId = sectionId + 44;
  const messageTextareaId = sectionId + 45;
  
  const submitButtonId = sectionId + 46;

  const elements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Courses Contact Section',
      parentId: null,
      className: sectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'courses-contact-section',
        'data-component': 'section'
      }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: containerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'contact-container'
      }
    },

    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: headerClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'contact-header'
      }
    },

    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Contact Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: titleClass,
      styles: {},
      children: [],
      text: 'Get in Touch',
      attributes: {
        'data-element': 'contact-title'
      }
    },

    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Contact Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: subtitleClass,
      styles: {},
      children: [],
      text: 'Have questions about our courses? Need help choosing the right program? We\'re here to help you start your learning journey.',
      attributes: {
        'data-element': 'contact-subtitle'
      }
    },

    // Content
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: contentClass,
      styles: {},
      children: [leftColumnId, rightColumnId],
      text: '',
      attributes: {
        'data-element': 'contact-content'
      }
    },

    // Left Column
    {
      id: leftColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Left Column',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: leftColumnClass,
      styles: {},
      children: [infoContainerId, socialContainerId],
      text: '',
      attributes: {
        'data-element': 'contact-left-column'
      }
    },

    // Right Column
    {
      id: rightColumnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Right Column',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: rightColumnClass,
      styles: {},
      children: [formContainerId],
      text: '',
      attributes: {
        'data-element': 'contact-right-column'
      }
    },

    // Contact Info Container
    {
      id: infoContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Contact Info Container',
      parentId: leftColumnId,
      hierarchyName: getHierarchyName('div'),
      className: infoContainerClass,
      styles: {},
      children: [addressItemId, phoneItemId, emailItemId],
      text: '',
      attributes: {
        'data-element': 'contact-info-container'
      }
    },

    // Address Item
    {
      id: addressItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: infoItemClass,
      styles: {},
      children: [addressIconId, addressContentId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },

    // Address Icon
    {
      id: addressIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Icon',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoIconClass,
      styles: {},
      children: [],
      text: '📍',
      attributes: {
        'data-element': 'contact-info-icon'
      }
    },

    // Address Content
    {
      id: addressContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Address Content',
      parentId: addressItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoContentClass,
      styles: {},
      children: [addressTitleId, addressTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-content'
      }
    },

    // Address Title
    {
      id: addressTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Address Title',
      parentId: addressContentId,
      hierarchyName: getHierarchyName('h4'),
      className: infoTitleClass,
      styles: {},
      children: [],
      text: 'Our Location',
      attributes: {
        'data-element': 'contact-info-title'
      }
    },

    // Address Text
    {
      id: addressTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Address Text',
      parentId: addressContentId,
      hierarchyName: getHierarchyName('p'),
      className: infoTextClass,
      styles: {},
      children: [],
      text: 'Chak#: 102 JB Burj Village Faisalabad, Pakistan',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },

    // Phone Item
    {
      id: phoneItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: infoItemClass,
      styles: {},
      children: [phoneIconId, phoneContentId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },

    // Phone Icon
    {
      id: phoneIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Icon',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoIconClass,
      styles: {},
      children: [],
      text: '📞',
      attributes: {
        'data-element': 'contact-info-icon'
      }
    },

    // Phone Content
    {
      id: phoneContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Phone Content',
      parentId: phoneItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoContentClass,
      styles: {},
      children: [phoneTitleId, phoneTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-content'
      }
    },

    // Phone Title
    {
      id: phoneTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Phone Title',
      parentId: phoneContentId,
      hierarchyName: getHierarchyName('h4'),
      className: infoTitleClass,
      styles: {},
      children: [],
      text: 'Call Us',
      attributes: {
        'data-element': 'contact-info-title'
      }
    },

    // Phone Text
    {
      id: phoneTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Phone Text',
      parentId: phoneContentId,
      hierarchyName: getHierarchyName('p'),
      className: infoTextClass,
      styles: {},
      children: [],
      text: '03012491171',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },

    // Email Item
    {
      id: emailItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Item',
      parentId: infoContainerId,
      hierarchyName: getHierarchyName('div'),
      className: infoItemClass,
      styles: {},
      children: [emailIconId, emailContentId],
      text: '',
      attributes: {
        'data-element': 'contact-info-item'
      }
    },

    // Email Icon
    {
      id: emailIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Icon',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoIconClass,
      styles: {},
      children: [],
      text: '✉️',
      attributes: {
        'data-element': 'contact-info-icon'
      }
    },

    // Email Content
    {
      id: emailContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Content',
      parentId: emailItemId,
      hierarchyName: getHierarchyName('div'),
      className: infoContentClass,
      styles: {},
      children: [emailTitleId, emailTextId],
      text: '',
      attributes: {
        'data-element': 'contact-info-content'
      }
    },

    // Email Title
    {
      id: emailTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Email Title',
      parentId: emailContentId,
      hierarchyName: getHierarchyName('h4'),
      className: infoTitleClass,
      styles: {},
      children: [],
      text: 'Email Us',
      attributes: {
        'data-element': 'contact-info-title'
      }
    },

    // Email Text
    {
      id: emailTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Email Text',
      parentId: emailContentId,
      hierarchyName: getHierarchyName('p'),
      className: infoTextClass,
      styles: {},
      children: [],
      text: 'snit.org@hotmail.com',
      attributes: {
        'data-element': 'contact-info-text'
      }
    },

    // Social Container
    {
      id: socialContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Container',
      parentId: leftColumnId,
      hierarchyName: getHierarchyName('div'),
      className: socialContainerClass,
      styles: {},
      children: [socialTitleId, socialLinksId],
      text: '',
      attributes: {
        'data-element': 'contact-social-container'
      }
    },

    // Social Title
    {
      id: socialTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Social Title',
      parentId: socialContainerId,
      hierarchyName: getHierarchyName('h3'),
      className: socialTitleClass,
      styles: {},
      children: [],
      text: 'Follow Our Learning Community',
      attributes: {
        'data-element': 'contact-social-title'
      }
    },

    // Social Links
    {
      id: socialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Links',
      parentId: socialContainerId,
      hierarchyName: getHierarchyName('div'),
      className: socialLinksClass,
      styles: {},
      children: [linkedinLinkId, twitterLinkId, youtubeLinkId, youtubeLinkId + 100],
      text: '',
      attributes: {
        'data-element': 'contact-social-links'
      }
    },

    // Facebook Link
    {
      id: linkedinLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Facebook Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [linkedinLinkId + 200],
      text: '',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://www.facebook.com/share/16Q7ppag35/',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Facebook'
      },
      linkTo: 'https://www.facebook.com/share/16Q7ppag35/'
    },

    // Facebook Icon
    {
      id: linkedinLinkId + 200,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Facebook Icon',
      parentId: linkedinLinkId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:facebook.svg?color=%236366f1',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Facebook'
      }
    },

    // WhatsApp Link
    {
      id: twitterLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'WhatsApp Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [twitterLinkId + 200],
      text: '',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://whatsapp.com/channel/0029Vb4nVx57oQhazj6xAt41',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'WhatsApp'
      },
      linkTo: 'https://whatsapp.com/channel/0029Vb4nVx57oQhazj6xAt41'
    },

    // WhatsApp Icon
    {
      id: twitterLinkId + 200,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'WhatsApp Icon',
      parentId: twitterLinkId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:whatsapp.svg?color=%236366f1',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'WhatsApp'
      }
    },

    // TikTok Link
    {
      id: youtubeLinkId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'TikTok Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [youtubeLinkId + 200],
      text: '',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://www.tiktok.com/@snitfaisalabad?_t=ZS-8wjL0zMp8HR&_r=1',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'TikTok'
      },
      linkTo: 'https://www.tiktok.com/@snitfaisalabad?_t=ZS-8wjL0zMp8HR&_r=1'
    },

    // TikTok Icon
    {
      id: youtubeLinkId + 200,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'TikTok Icon',
      parentId: youtubeLinkId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/simple-icons:tiktok.svg?color=%236366f1',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'TikTok'
      }
    },

    // Instagram Link
    {
      id: youtubeLinkId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Instagram Link',
      parentId: socialLinksId,
      hierarchyName: getHierarchyName('a'),
      className: socialLinkClass,
      styles: {},
      children: [youtubeLinkId + 300],
      text: '',
      attributes: {
        'data-element': 'contact-social-link',
        'href': 'https://www.instagram.com/snitfaisalabad?igsh=MW5hMXNnNnJtZjg1bw==',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Instagram'
      },
      linkTo: 'https://www.instagram.com/snitfaisalabad?igsh=MW5hMXNnNnJtZjg1bw=='
    },

    // Instagram Icon
    {
      id: youtubeLinkId + 300,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instagram Icon',
      parentId: youtubeLinkId + 100,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '20px',
        height: '20px'
      },
      src: 'https://api.iconify.design/mdi:instagram.svg?color=%236366f1',
      children: [],
      text: '',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Instagram'
      }
    },

    // Form Container
    {
      id: formContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Form Container',
      parentId: rightColumnId,
      hierarchyName: getHierarchyName('div'),
      className: formContainerClass,
      styles: {},
      children: [formTitleId, formId],
      text: '',
      attributes: {
        'data-element': 'contact-form-container'
      }
    },

    // Form Title
    {
      id: formTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Form Title',
      parentId: formContainerId,
      hierarchyName: getHierarchyName('h3'),
      className: formTitleClass,
      styles: {},
      children: [],
      text: 'Send Us a Message',
      attributes: {
        'data-element': 'contact-form-title'
      }
    },

    // Form
    {
      id: formId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'form',
      name: 'Contact Form',
      parentId: formContainerId,
      hierarchyName: getHierarchyName('form'),
      className: formClass,
      styles: {},
      children: [nameEmailRowId, subjectGroupId, messageGroupId, submitButtonId],
      text: '',
      attributes: {
        'data-element': 'contact-form',
        'method': 'post',
        'action': ''
      }
    },

    // Name and Email Row
    {
      id: nameEmailRowId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name and Email Row',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formRowClass,
      styles: {},
      children: [nameGroupId, emailGroupId],
      text: '',
      attributes: {
        'data-element': 'contact-form-row'
      }
    },

    // Name Group
    {
      id: nameGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Name Group',
      parentId: nameEmailRowId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [nameLabelId, nameInputId],
      text: '',
      attributes: {
        'data-element': 'contact-form-group'
      }
    },

    // Name Label
    {
      id: nameLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Name Label',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Full Name',
      attributes: {
        'data-element': 'contact-form-label',
        'for': 'contact-name'
      }
    },

    // Name Input
    {
      id: nameInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Name Input',
      parentId: nameGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'contact-form-input',
        'type': 'text',
        'id': 'contact-name',
        'name': 'name',
        'placeholder': 'Enter your full name',
        'required': 'true'
      }
    },

    // Email Group
    {
      id: emailGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Email Group',
      parentId: nameEmailRowId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [emailLabelId, emailInputId],
      text: '',
      attributes: {
        'data-element': 'contact-form-group'
      }
    },

    // Email Label
    {
      id: emailLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Email Label',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Email Address',
      attributes: {
        'data-element': 'contact-form-label',
        'for': 'contact-email'
      }
    },

    // Email Input
    {
      id: emailInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Email Input',
      parentId: emailGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'contact-form-input',
        'type': 'email',
        'id': 'contact-email',
        'name': 'email',
        'placeholder': 'Enter your email address',
        'required': 'true'
      }
    },

    // Subject Group
    {
      id: subjectGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Subject Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [subjectLabelId, subjectInputId],
      text: '',
      attributes: {
        'data-element': 'contact-form-group'
      }
    },

    // Subject Label
    {
      id: subjectLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Subject Label',
      parentId: subjectGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Subject',
      attributes: {
        'data-element': 'contact-form-label',
        'for': 'contact-subject'
      }
    },

    // Subject Input
    {
      id: subjectInputId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'input',
      name: 'Subject Input',
      parentId: subjectGroupId,
      hierarchyName: getHierarchyName('input'),
      className: formInputClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'contact-form-input',
        'type': 'text',
        'id': 'contact-subject',
        'name': 'subject',
        'placeholder': 'What can we help you with?',
        'required': 'true'
      }
    },

    // Message Group
    {
      id: messageGroupId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Message Group',
      parentId: formId,
      hierarchyName: getHierarchyName('div'),
      className: formGroupClass,
      styles: {},
      children: [messageLabelId, messageTextareaId],
      text: '',
      attributes: {
        'data-element': 'contact-form-group'
      }
    },

    // Message Label
    {
      id: messageLabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'label',
      name: 'Message Label',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('label'),
      className: formLabelClass,
      styles: {},
      children: [],
      text: 'Your Message',
      attributes: {
        'data-element': 'contact-form-label',
        'for': 'contact-message'
      }
    },

    // Message Textarea
    {
      id: messageTextareaId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'textarea',
      name: 'Message Textarea',
      parentId: messageGroupId,
      hierarchyName: getHierarchyName('textarea'),
      className: formTextareaClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'contact-form-textarea',
        'id': 'contact-message',
        'name': 'message',
        'placeholder': 'Tell us about your learning goals or ask any questions...',
        'required': 'true',
        'rows': '5'
      }
    },

    // Submit Button
    {
      id: submitButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Submit Button',
      parentId: formId,
      hierarchyName: getHierarchyName('button'),
      className: formButtonClass,
      styles: {},
      children: [],
      text: 'Send Message',
      attributes: {
        'data-element': 'contact-form-button',
        'type': 'submit'
      }
    }
  ];

  return {
    contactSection: elements[0],
    elements: elements
  };
}; 